import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { formatCompactNumber } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import type { AssistantContextUsageEstimate } from "../types";

interface AssistantContextUsageBadgeProps {
  usage: AssistantContextUsageEstimate | null;
  compact?: boolean;
}

/** Context fraction at which the ring + label flip to the coral signal color. */
const HIGH_USAGE_THRESHOLD = 75;
const RING_RADIUS = 7;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/**
 * Context-usage indicator (redesign v2 state 9): a 10px donut ring + percentage that lives
 * in the composer footer. The arc fills clockwise from 12 o'clock; at >= 75% the arc and label
 * flip to coral — the one place color is allowed besides the send/accept/stop buttons. Hover or
 * focus reveals a minimal light, square tooltip with just the percentage and token ratio.
 *
 * 100% is where COMPACTION FIRES, not where the model's window ends. The backend computes both the
 * percentage and `contextLimitTokens` against `autoCompactTokenLimit` (90% of the window), so the
 * ring, the percentage and the ratio all share one denominator. Measured against the raw window
 * instead, a thread on a 1,000,000-token model would read "24%" on the very turn its history got
 * rewritten — a number that tells the user nothing about the thing they are about to notice.
 */
export function AssistantContextUsageBadge({
  usage,
  compact = false,
}: AssistantContextUsageBadgeProps) {
  const percent = usage?.estimatedContextPercent ?? null;
  // `null` rather than a placeholder: the ratio line is dropped entirely when either half is
  // unknown, so an em dash here would render "12.3k / —" where there used to be nothing.
  const tokenLabel =
    usage?.estimatedInputTokens != null ? formatCompactNumber(usage.estimatedInputTokens) : null;
  const limitLabel =
    usage?.contextLimitTokens != null ? formatCompactNumber(usage.contextLimitTokens) : null;
  const tokenRatioLabel = tokenLabel && limitLabel ? `${tokenLabel} / ${limitLabel}` : null;
  // The number is normally the provider's own reported size for the previous request plus the new
  // messages, and only falls back to counting characters when the prompt prefix changed (a
  // compaction, a model switch). Worth one line of tooltip, because the two differ in accuracy.
  const basisLabel =
    usage?.estimateBasis === "provider_anchor"
      ? "Measured from the provider's last reported request"
      : usage?.estimateBasis === "chars"
        ? "Estimated from prompt size"
        : null;

  const clampedPercent = Math.min(100, Math.max(0, percent ?? 0));
  const isHigh = (percent ?? 0) >= HIGH_USAGE_THRESHOLD;
  const dashOffset = RING_CIRCUMFERENCE * (1 - clampedPercent / 100);

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <div
            className={cn(
              "flex items-center text-[11px] font-medium tabular-nums",
              compact ? "gap-1.5" : "gap-2",
              isHigh ? "text-accent-coral" : "text-neutral-dim",
              !usage && "opacity-70"
            )}
          />
        }
      >
        <svg viewBox="0 0 20 20" className="size-2.5 shrink-0" aria-hidden="true">
          <circle
            cx="10"
            cy="10"
            r={RING_RADIUS}
            fill="none"
            strokeWidth={5}
            className="stroke-border"
          />
          <circle
            cx="10"
            cy="10"
            r={RING_RADIUS}
            fill="none"
            strokeWidth={5}
            strokeLinecap="butt"
            stroke="currentColor"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
            transform="rotate(-90 10 10)"
          />
        </svg>
        <span>{percent === null ? "--" : `${percent}%`}</span>
      </TooltipTrigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Positioner side="top" align="end" sideOffset={8} className="isolate z-50">
          <TooltipPrimitive.Popup className="bg-popover text-popover-foreground ring-foreground/10 z-50 w-fit max-w-xs rounded-lg p-2.5 text-xs shadow-md ring-1">
            {usage ? (
              <div className="space-y-0.5">
                <div>
                  {percent === null ? "Context estimate pending" : `${percent}% context used`}
                </div>
                {tokenRatioLabel ? (
                  <div className="text-muted-foreground">
                    {tokenRatioLabel} tokens before compaction
                  </div>
                ) : null}
                {basisLabel ? <div className="text-muted-foreground">{basisLabel}</div> : null}
              </div>
            ) : (
              "Context estimate appears after Claudia prepares the first request."
            )}
          </TooltipPrimitive.Popup>
        </TooltipPrimitive.Positioner>
      </TooltipPrimitive.Portal>
    </Tooltip>
  );
}
